import ChevronRight from "@DSG/MobiusOverrides/Icons/ChevronRight";
import Phone from "@DSG/MobiusOverrides/Icons/Phone";
import { MappedLink } from "@DSG/MobiusOverrides/Menu/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@DSG/MobiusOverrides/PanelMenu/PanelMenu";
import colorPallete, {
    bodyFonts,
    buttonFonts,
    elevation,
    labelFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getDealersDataView } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName, NavigationModeMenu } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import { Clickable } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import size from "lodash/size";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const headerLinkListFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/LinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const myAccountSequence = getSettingsCollection(state).customSettings;

    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;

    return {
        myAccountSequence,
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        firstName: state.context.session?.firstName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        myListPageLink: getPageLinkByPageType(state, "MyListsPage"),
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        getDealersParameter: state.pages.locationFinder.getDealersParameter,
        dealersDataView: getDealersDataView(state, state.pages.locationFinder.getDealersParameter),
        isAuthenticated: state.context.session.isAuthenticated,
        pickUpWarehouse:
            state.context.session.pickUpWarehouse || state.pages.accountSettings.editingAccount?.defaultWarehouse,
        headerLinkListFields,
        displayModeNavigation: orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),

    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    defaultLanguageId?: any;
    currentLanguageId?: any;
    id?: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        size: 32,
        css: css`
            .menu-trigger-button-icon svg {
                color: ${colorPallete.grey800};
            }
            &:hover {
                outline: none;
            }
        `
    },
    drawer: {
        size: 342,
        cssOverrides: {
            drawerContainer: css`
                background: ${colorPallete.grey600};
                
                .storeModeSwitcher {
                    font: ${bodyFonts.body1SemiBoldLg.fontWeight} ${bodyFonts.body1SemiBoldLg.fontSize}/${bodyFonts.body1SemiBoldLg.lineHeight} ${bodyFonts.body1SemiBoldLg.fontFamily};
                    svg {
                        width: 16px;
                        height: 16px;
                        margin-left: 8px;
                    }
                }
            
                &.websiteHeaderDrawer {
                    .storeModeSwitcherWrapper {
                        border-top: 1px solid ${colorPallete.grey300};
                        padding-top: 24px;
                        margin-top: 24px;
                    }
                }
                &.vmiHeaderDrawer {
                    > div {
                        background: ${colorPallete.secondaryNavy500};
                    }
                    .main-navigation-row,
                    .sign-out-row,
                    [class*="DrawerTitle"],
                    [class*="DrawerContent"] {
                        background: transparent;
                    }
                    [data-test-selector="closeDrawer"] [class*="ButtonIcon"],
                    .main-navigation-row > span,
                    .welcome-username,
                    .storeModeSwitcher {
                        color: ${colorPallete.white};
                    }
                    .sign-out-row {
                        > span {
                            background: ${colorPallete.white};
                            border-color: ${colorPallete.grey800};
                            color: ${colorPallete.grey800};
                        }
                    }

                    .drawer-section-wrapper {
                        &:last-child {
                            border-top: 1px solid ${colorPallete.grey300};
                            padding-top: 8px;
                        }
                    }
                    [class*="DrawerContent"] {
                        .drawer-section-wrapper {
                            &:first-child {
                                padding-top: 24px;
                                margin-top: 24px;
                                border-top: 1px solid ${colorPallete.grey300};
                            }
                        }
                    }
                }
            `,
            drawerBody: css`
                background: ${colorPallete.white};
            `,
            drawerContent: css`
                margin-top: 0;
                overflow-x: hidden;
                background: ${colorPallete.white};
            `,
            titleButton: css`
                background: none;
                padding: 0 !important;

                [class*="ButtonIcon"] {
                    color: ${colorPallete.grey800};
                }
            `,
            drawerTitle: css`
                padding-top: 24px;
                [data-test-selector="closeDrawer"] {
                    min-height: initial;
                }
            `
        },
    },
    drawerSectionWrapper: {
        css: css`
            padding: 24px 0;
            width: calc(100% - 48px);
            margin: 0 auto;
            border-bottom: 1px solid ${colorPallete.grey300};
            & > nav {
                &:last-child > button {
                    padding-bottom: 0;
                }
            }
            .location-finder {
                font-size: ${buttonFonts.buttonLg.fontSize};
                line-height: ${buttonFonts.buttonLg.fontSize};
                font-weight: ${buttonFonts.buttonLg.fontWeight};
                padding-bottom: 16px;
            }

            &.drawer-four {
                padding-bottom: 16px;
            }

            &:first-child {
                padding-top: 16px;
            }

            &:nth-last-child(2),
            &:last-child {
                border-bottom: none;
            }

            &:last-of-type {
                padding-top: 0;
                padding-bottom: 40px;
            }

            .branch-details-wrapper {
                display: flex;
                align-items: flex-start;
                gap: 8px;
            }

            .branch-name,
            .branch-heading {
                display: block;
                font-size: ${labelFonts.label1Regular.fontSize};
                line-height: ${labelFonts.label1Regular.lineHeight};
                font-weight: ${labelFonts.label1Regular.fontWeight};

                &.text-bold {
                    font-weight: ${labelFonts.label2SemiBold.fontWeight};
                }
            }

            [class*="StyledClickable"] {
                display: block;
            }

            .mobile-parent-category-link {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 10px 24px;
                margin-top: 24px;
                margin-bottom: 16px;

                .parent-title-mobile {
                    font-size: ${bodyFonts.body2SemiBoldLg.fontSize};
                    line-height: ${bodyFonts.body2SemiBoldLg.lineHeight};
                    font-weight: ${bodyFonts.body2SemiBoldLg.fontWeight};
                }

                a {
                    flex-shrink: 0;
                    font-size: ${buttonFonts.buttonSm.fontSize};
                    line-height: ${buttonFonts.buttonSm.lineHeight};
                    font-weight: ${buttonFonts.buttonSm.fontWeight};
                }
            }

            .mobile-menu-header {
                border-bottom: 1px solid ${colorPallete.grey300};
                padding: 24px 0;
                max-width: calc(100% - 48px);
                margin: 0 auto;
            }
        `,
    },
    panelMenu: {
        closeIconProps: {
            color: colorPallete.grey800,
        },
        cssOverrides: {
            wrapper: css`
                > button {
                    width: calc(100% - 2px);
                    background: ${colorPallete.white};
                    color: ${colorPallete.grey800};

                    [class*="IconWrapper"] {
                        width: 22px;
                        height: 22px;
                    }

                    & + [class*="PanelChildren"] {
                        [class*="PanelMenuStyle"] {
                            [class*="PanelRowStyle"] {
                                background: ${colorPallete.white};
                                color: ${colorPallete.grey800};
                            }
                        }
                    }
                }
            `,
        },
        childTypographyProps: {
            size: bodyFonts.body2RegularSm.fontSize,
            weight: bodyFonts.body2RegularSm.fontWeight,
            lineHeight: bodyFonts.body2RegularSm.lineHeight,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        color: "common.background",
    },
    punchOutUserRowText: {
        color: "common.background",
    },
    mainNavigationRow: {
        color: colorPallete.white,
        css: css`
            margin: 0;
            padding: 0;

            &:not(:last-child) {
                padding-bottom: 16px;
            }
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        color: colorPallete.grey800,
        weight: themeFontWeights.semiBold,
        size: buttonFonts.buttonLg.fontSize,
        lineHeight: buttonFonts.buttonLg.lineHeight,
    },
    logoLinks: {
        color: colorPallete.white,
        typographyProps: {
            weight: buttonFonts.buttonLg.fontWeight,
            size: buttonFonts.buttonLg.fontSize,
            lineHeight: buttonFonts.buttonLg.lineHeight,
            ellipsis: true,
        },
        css: css`
            padding: 0;
            &:not(:last-child) {
                padding-bottom: 16px;
            }
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 24px;
            `,
        },
    },
    signOutRow: {
        css: css`
            background: ${colorPallete.white};
            padding: 0;
            margin-top: 24px;
            width: 100%;
        `,
    },
    signOutRowText: {
        mergeCss: true,
        css: css`
            display: block;
            width: 100%;
            text-align: center;
        `,
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    links,
    showQuickOrder,
    quickOrderLink,
    displayVmiNavigation,
    displayModeNavigation,
    vmiPageLinks,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    languages,
    currentLanguage,
    currentLocation,
    userName,
    firstName,
    isGuest,
    myAccountPageLink,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    showCustomerMenuItem,
    fulfillmentLabel,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    cart,
    history,
    dealersDataView,
    isAuthenticated,
    pickUpWarehouse,
    theme,
    displayPricingAndInventoryToggles,
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    visibilityState,
    headerLinkListFields,
    myListPageLink,
    defaultLanguageId,
    currentLanguageId,
    myAccountSequence,
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const headerLinkList = useGetLinks(headerLinkListFields, o => o.fields.destination);
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        closeDrawer();
    };

    const signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }

            closeDrawer();
        }
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };
    const settings = myAccountSequence as any;
    const myAccountMenuItems = settings.myAccountLinkMobile?.split(",");
    const myAccountMenuPages = [] as PageLinkModel[];
    myAccountMenuItems?.forEach((item: string) => {
        const menuLink = myAccountPageLink?.children?.find(pageLink => pageLink.type === item);
        if (menuLink !== undefined) {
            myAccountMenuPages.push(menuLink);
        }
    });

    let orderHistoryPages;
    if (myAccountMenuPages && myListPageLink && myAccountPageLink) {
        orderHistoryPages = myAccountMenuPages.filter(item => item.type === "OrderHistoryPage");
        orderHistoryPages.push(myListPageLink);
        orderHistoryPages.push(myAccountPageLink);
    }

    const [currentDealer, setCurrentDealer] = React.useState<LocationModel | undefined>(undefined);

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    return (
        <>
            <Button
                className="menu-trigger-button"
                onClick={openDrawer}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileMenu"
            >
                <ButtonIcon className="menu-trigger-button-icon" src={Menu} size={32} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                className={`drawer ${displayVmiNavigation ? 'vmiHeaderDrawer' : 'websiteHeaderDrawer'}`}
                draggable
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {!displayVmiNavigation && (
                        <>
                            <StyledWrapper className="branch-details-wrapper">
                                {isAuthenticated && (
                                    <div>
                                        <Icon src={Phone} size={16}></Icon>
                                    </div>
                                )}
                                {!isAuthenticated && currentDealer && (
                                    <div>
                                        <Icon src={Phone} size={16}></Icon>
                                    </div>
                                )}
                                {!isAuthenticated &&
                                    !currentDealer &&
                                    dealersDataView &&
                                    dealersDataView.value &&
                                    dealersDataView.value.length > 0 && (
                                        <div>
                                            <Icon src={Phone} size={16}></Icon>
                                        </div>
                                    )}
                                <div>
                                    {isAuthenticated && (
                                        <span className="closest-title">
                                            <p className="branch-heading">{translate("Your Branch: ")}</p>
                                            <span className="branch-name text-bold">
                                                {pickUpWarehouse?.name.replace(/\w+/g, function (w) {
                                                    return w[0].toUpperCase() + w.slice(1).toLowerCase();
                                                })}
                                                , {pickUpWarehouse?.postalCode}{" "}
                                                <Link href={`tel:${pickUpWarehouse?.phone}`} target="_blank">
                                                    {pickUpWarehouse?.phone}
                                                </Link>{" "}
                                            </span>
                                        </span>
                                    )}
                                    {!isAuthenticated && (
                                        <span className="closest-title">
                                            <>
                                                {currentDealer && (
                                                    <>
                                                        <p className="branch-heading">{translate("Branch closest to you: ")}</p>
                                                        <span className="branch-name text-bold">
                                                            {currentDealer.name.replace(/\w+/g, function (w) {
                                                                return w[0].toUpperCase() + w.slice(1).toLowerCase();
                                                            })}
                                                            , {currentDealer.postalCode}{" "}
                                                            <Link href={`tel:${currentDealer.phone}`} target="_blank">
                                                                {currentDealer.phone}
                                                            </Link>{" "}
                                                        </span>
                                                    </>
                                                )}
                                                {!currentDealer &&
                                                    dealersDataView &&
                                                    dealersDataView.value &&
                                                    dealersDataView.value.length > 0 && (
                                                        <>
                                                            <p className="branch-heading">
                                                                {translate("Branch closest to you: ")}
                                                            </p>
                                                            <span className="branch-name text-bold">
                                                                {dealersDataView.value[0].name.replace(/\w+/g, function (w) {
                                                                    return w[0].toUpperCase() + w.slice(1).toLowerCase();
                                                                })}
                                                                , {dealersDataView.value[0].postalCode}{" "}
                                                                <Link
                                                                    href={`tel:${dealersDataView.value[0].phone}`}
                                                                    target="_blank"
                                                                >
                                                                    {dealersDataView.value[0].phone}
                                                                </Link>{" "}
                                                            </span>
                                                        </>
                                                    )}
                                            </>
                                        </span>
                                    )}
                                </div>
                            </StyledWrapper>
                        </>
                    )}

                    {displayModeSwitch && (
                        <StyledWrapper className="storeModeSwitcherWrapper">
                            {currentMode === "Vmi" && (
                                <Clickable
                                    className="storeModeSwitcher"
                                    onClick={() => {
                                        navigationModeChangeHandler("Storefront");
                                        closeDrawer();
                                    }}
                                >
                                    {translate("Switch to DSG Website")}
                                    <ChevronRight/>
                                </Clickable>
                            )}

                            {currentMode !== "Vmi" && (
                                <Clickable
                                    className="storeModeSwitcher"
                                    onClick={() => {
                                        navigationModeChangeHandler("Vmi");
                                        closeDrawer();
                                    }}
                                >
                                    {translate("Switch to VMI Dashboard")}
                                    <ChevronRight/>
                                </Clickable>
                            )}
                        </StyledWrapper>
                    )}
                 
                    {!userName && !isGuest && !isPunchOutSession && (
                        <PanelRow
                            className="sign-out-row"
                            onClick={signInPanelClickHandler}
                            href={signInUrl?.url}
                            {...styles.signOutRow}
                        >
                            <Typography className="sign-out-row-text btn btn-secondary" {...styles.signOutRowText}>
                                {isPunchOutSession
                                    ? translate("Cancel PunchOut")
                                    : translate("Sign in / create account")}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
                
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation && (
                        <Link href="/LocationFinder" className="location-finder" onClick={closeDrawer}>
                            {translate('Find Location')}
                        </Link>
                    )}
                  
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            if (link.children && link.children.length > 0) {
                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        parentCategoryName={link.title}
                                        parentCategoryUrl={link.url}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                hasChildren
                                                {...styles.mainNavigationRow}
                                            >
                                                <Typography
                                                    className="main-navigation-row-text"
                                                    {...styles.mainNavigationRowTypography}
                                                >
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        {...styles.panelMenu}
                                        defaultLanguageId={defaultLanguageId}
                                        currentLanguageId={currentLanguageId}
                                        parentNodeId={link.id}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );
                        })}

                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>
                {!displayVmiNavigation && myAccountMenuPages.length > 0 && (
                    <StyledSection className="drawer-section-wrapper drawer-three" {...styles.drawerSectionWrapper}>
                        {orderHistoryPages?.map((link, index) => (
                            <PanelRow
                                className="logo-links-row"
                                key={link.title + String(index)}
                                {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                isCurrent={currentPageUrl === link.url}
                                onClick={closeDrawer}
                                onKeyPress={(event: React.KeyboardEvent) => {
                                    if (event.key === " " && history && link?.url) {
                                        event.preventDefault();
                                        closeDrawer();
                                        history.push(link.url);
                                    }
                                }}
                                href={link.url}
                            >
                                <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                    {link.type === "OrderHistoryPage"
                                        ? translate("Reorder Items")
                                        : link.type === "MyAccountPage"
                                        ? translate("Account Overview")
                                        : link.title}
                                </Typography>
                            </PanelRow>
                        ))}
                        {isAuthenticated && (
                            <>
                                {myAccountMenuPages.map((link, index) => (
                                    <PanelRow
                                        className="logo-links-row"
                                        key={link.title + String(index)}
                                        {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        onKeyPress={(event: React.KeyboardEvent) => {
                                            if (event.key === " " && history && link?.url) {
                                                event.preventDefault();
                                                closeDrawer();
                                                history.push(link.url);
                                            }
                                        }}
                                        href={link.url}
                                    >
                                        <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ))}
                                {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        isCurrent={currentPageUrl === quickOrderLink.url}
                                        onClick={closeDrawer}
                                        href={quickOrderLink.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {quickOrderLink.title}
                                        </Typography>
                                    </PanelRow>
                                )}
                            </>
                        )}
                    </StyledSection>
                )}

                {!displayVmiNavigation && headerLinkList.length > 0 && (
                    <StyledSection className="drawer-section-wrapper drawer-four" {...styles.drawerSectionWrapper}>
                        {headerLinkList.map((link, index) => (
                            <PanelRow
                                className="logo-links-row"
                                key={link.title + String(index)}
                                {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                isCurrent={currentPageUrl === link.url}
                                onClick={closeDrawer}
                                onKeyPress={(event: React.KeyboardEvent) => {
                                    if (event.key === " " && history && link?.url) {
                                        event.preventDefault();
                                        closeDrawer();
                                        history.push(link.url);
                                    }
                                }}
                                href={link.url}
                                target={headerLinkListFields[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                    {headerLinkListFields[index].fields.overriddenTitle || link.title}
                                </Typography>
                            </PanelRow>
                        ))}
                    </StyledSection>
                )}
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <SelectorMenu
                        options={currencies?.map(c => {
                            return {
                                title: c.currencyCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setCurrency({ currencyId: c.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        closeModal={closeDrawer}
                        currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                        currentOptionIcon={<Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>}
                    />
                    <SelectorMenu
                        dataTestSelector="mobileLanguageSelector"
                        options={languages?.map(l => {
                            return {
                                title: l.languageCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setLanguage({ languageId: l.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        currentOption={currentLanguage?.languageCode}
                        closeModal={closeDrawer}
                        currentOptionIcon={
                            currentLanguage?.imageFilePath ? (
                                <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                            ) : (
                                <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                            )
                        }
                    />
         
                    {displayPricingAndInventoryToggles && (
                        <>
                            <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                            <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                        </>
                    )}
                    {userName && !isGuest && (
                        <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                            <Typography className="sign-out-row-text btn btn-secondary" {...styles.signOutRowText}>
                                {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const getStoreFrontCurrentMode = getCookie("NavigationModeCookieName") || "Storefront";

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className={`panel-menu`}
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};
/* overriding file with sandbox file */
function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
